<template>
    <FooterBody
        v-if="isFooterView && footer.uuid"
        id="view-footer"
        :footer="footer"
        @updateIframeHeight="updateHeight"
    />
    <TermsBody
        v-if="isTermsView && footer.uuid"
        id="view-terms"
        :footer="footer"
        @updateIframeHeight="updateHeight"
    />
    <TermsEmbedBody
        v-if="isTermsEmbedView && footer.uuid"
        id="view-terms-embed"
        :footer="footer"
        @updateIframeHeight="updateHeight"
    />
    <PolicyBody
        v-if="isPolicyView && footer.uuid"
        id="view-policy"
        :footer="footer"
        @updateIframeHeight="updateHeight"
    />
    <PolicyEmbedBody
        v-if="isPolicyEmbedView && footer.uuid"
        id="view-policy-embed"
        :footer="footer"
        @updateIframeHeight="updateHeight"
    />
    <AboutBody
        v-if="isAboutView && footer.uuid"
        id="view-about"
        :footer="footer"
        @updateIframeHeight="updateHeight"
    />
</template>

<script>
import FooterBody from "@/views/tools/legal/footers/FooterBody.vue";
import TermsBody from "@/views/tools/legal/terms/TermsBody.vue";
import TermsEmbedBody from "@/views/tools/legal/terms/TermsEmbedBody.vue";
import PolicyBody from "@/views/tools/legal/policies/PolicyBody.vue";
import PolicyEmbedBody from "@/views/tools/legal/policies/PolicyEmbedBody.vue";
import AboutBody from "@/views/tools/legal/about/AboutBody.vue";
import { footersService } from "@/apps/legal/services/footers.service";
import { statsService } from "@/shared/statistics/stats.service";
import { setFontFamily, updateIframeHeight } from "@/shared/utils";
import { prepareFooter } from "@/shared/projects/prepareData";
import { RENDER_ROUTE_NAMES } from "./render.routes";
import { usePreviewLanguage } from "@/shared/utils/router/usePreviewLanguage";
import { projectsService } from "@/shared/projects/projects.service";

export default {
    name: "LegalPage",
    components: {
        FooterBody,
        TermsBody,
        TermsEmbedBody,
        PolicyBody,
        PolicyEmbedBody,
        AboutBody,
    },
    data() {
        return {
            footer: {},
            windowWidth: window.width,
            currentFont: "Inter",
            waiting: false,
        };
    },
    computed: {
        isFooterView() {
            return this.$route.name === RENDER_ROUTE_NAMES.legalFooter;
        },
        isTermsView() {
            return this.$route.name === RENDER_ROUTE_NAMES.legalTerms;
        },
        isTermsEmbedView() {
            return this.$route.name === RENDER_ROUTE_NAMES.legalTermsEmbed;
        },
        isPolicyView() {
            return this.$route.name === RENDER_ROUTE_NAMES.legalPrivacy;
        },
        isPolicyEmbedView() {
            return this.$route.name === RENDER_ROUTE_NAMES.legalPrivacyEmbed;
        },
        isAboutView() {
            return this.$route.name === RENDER_ROUTE_NAMES.legalAbout;
        },
    },
    setup() {
        const { setLanguageIfNoQuery } = usePreviewLanguage();
        return { setLanguageIfNoQuery };
    },
    async mounted() {
        const { projectUuid, legalUuid } = this.$route.params;
        if (this.isFooterView) {
            this.footer = await this.loadFooter(projectUuid);
        } else {
            this.footer = await this.loadFooterByLegal(legalUuid);
        }

        //in case there is no lang in query chose project ownwer's language
        this.setLanguageIfNoQuery(this.footer?.user?.language);

        this.adjustIframe();

        window.addEventListener("resize", this.resizeIframe);

        this.saveStat();
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.resizeIframe);
    },
    methods: {
        async loadFooter(uuid) {
            this.waiting = true;
            try {
                const footer = await projectsService.getOneForRender({ tool: "legal", uuid });
                this.waiting = false;
                return prepareFooter(footer);
            } catch {
                this.waiting = false;
                this.$router.push({ name: RENDER_ROUTE_NAMES.notFound });
            }
        },
        async loadFooterByLegal(uuid) {
            this.waiting = true;
            try {
                const footer = await footersService.getOneByLegalUuidForRender(uuid);
                this.waiting = false;
                return prepareFooter(footer);
            } catch {
                this.waiting = false;
                this.$router.push({ name: RENDER_ROUTE_NAMES.notFound });
            }
        },
        async adjustIframe() {
            if (!this.footer.id) return;
            this.currentFont = setFontFamily(this.footer.fontFamily, this.currentFont);
            await this.$nextTick();
            this.updateHeight();
        },
        updateHeight() {
            updateIframeHeight("footer", this.footer.uuid);
        },
        resizeIframe() {
            if (window.innerWidth !== this.windowWidth) {
                this.adjustIframe();
                this.windowWidth = window.innerWidth;
            }
        },
        async saveStat() {
            const { id, uuid } = this.footer;
            let urlView = "";
            if (this.isTermsView) {
                urlView = "terms";
            }
            if (this.isPolicyView) {
                urlView = "policy";
            }
            if (this.isAboutView) {
                urlView = "about";
            }
            await statsService.saveLegalStats(id, uuid, urlView);
        },
    },
};
</script>
